import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { variance } from 'mathjs';
import get from 'lodash.get';
import { createSelector } from 'reselect';
import { colorFromTag, scalarIfUnitArray, getSegmentTagStyles } from 'src/functions';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */

import {
  Container,
  Col,
  Div,
  ExpanderColumn,
  Icon,
  Modal,
  Row,
  Table,
  Tag,
  Text,
  Textarea,
} from '@pinpoint/design-kit';
import { components, charts } from '@pinpoint/onpoint-charts';
import DEFAULT_THEME from 'src/styles/DefaultTheme';

import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';
import { appActions, bcActions, copyPxActions } from 'src/store/actions';
import {
  Message,
  ProgressBar,
  PxTextEditor,
  GridWrapper,
  ApiWrapper,
  TimedPopup,
  Switch,
  Button,
} from 'src/components';
import { Rbac, GraphicPage, SegmentsViewFlyover } from 'src/containers';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const selectedSegmentIdsFilter = ({ copyPx: { selectedSegmentIds } }) => selectedSegmentIds;
const selectedClusterIdsFilter = ({ copyPx: { selectedClusterIds } }) => selectedClusterIds;

const selectedSegmentIdSelector = createSelector([selectedSegmentIdsFilter], (selectedSegmentIds) =>
  selectedSegmentIds.length === 1 ? scalarIfUnitArray(selectedSegmentIds) : null
);

const selectedClusterIdSelector = createSelector([selectedClusterIdsFilter], (selectedClusterIds) =>
  selectedClusterIds.length === 1 ? scalarIfUnitArray(selectedClusterIds) : null
);

const isAnySelectedSelector = createSelector(
  [selectedSegmentIdsFilter, selectedClusterIdsFilter],
  (selectedSegmentIds, selectedClusterIds) => selectedSegmentIds.length + selectedClusterIds.length > 0
);

const Radar = components.withFixedTitle(charts.Radar);
const Gauge = components.withFixedTitle(charts.Gauge);

const DEFAULT_SEGMENT_THEME = 'light';

const getSegmentTheme = (segments, segmentId) => {
  const segment = segments.find(({ id }) => segmentId === id);
  if (segment) {
    if (segment.theme === DEFAULT_SEGMENT_THEME) {
      return 'theme_pinpoint';
    }
    return colorFromTag(segment);
  }
  return 'theme_pinpoint';
};

const getClusterTheme = (segments, segmentId, clusterId) => {
  const cluster = segments.find(({ id }) => segmentId === id).clusters.find(({ id }) => clusterId === id);
  if (cluster) {
    if (cluster.theme === DEFAULT_SEGMENT_THEME) {
      return 'theme_pinpoint';
    }
    return `theme_${cluster.theme}`;
  }
  return 'theme_pinpoint';
};

function CopyPx({
  history,
  app: {
    profile: { role, tenant },
  },
  copyPx: {
    highValueWords,
    fetchHighValueWordsStatus,
    segments,
    fetchSegmentsStatus,
    selectedSegmentIds,
    selectedClusterIds,
    gridState,
    controlledGridState,
    maxGridRowSelection,
    copyPxScores,
  },
  reset,
  fetchSegments,
  fetchHighValueWords,
  setSegmentId,
  selectGridRow,
  deselectGridRow,
  setGridState,
  deselectAllGridRows,
  setCopyPx,
  editSegment,
}) {
  const isAnySelected = useSelector(isAnySelectedSelector);
  const selectedSegmentId = useSelector(selectedSegmentIdSelector);
  const selectedClusterId = useSelector(selectedClusterIdSelector);
  /* eslint-disable react/prop-types */

  const [mode, setMode] = useState('ANCHOR_TRAITS');
  const [score, setScore] = useState(0);
  const [model, setModel] = useState(null);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const pxTextEditorRef = useRef(null);

  const columns = useMemo(
    () => [
      ExpanderColumn,
      {
        Header: 'Name',
        id: 'name',
        justify: 'left',
        Cell: CellText,
        accessor: (segment) => (segment.filter === 'POPULATION_SNAPSHOT' ? segment.dataset.name : segment.name),
      },
      {
        Header: 'Tag',
        justify: 'left',
        accessor: ({ tag }) => tag || '',
        Cell: CellTag,
      },
      {
        Header: 'Onboarded',
        justify: 'center',
        cellJustify: 'center',
        accessor: (entity) => dayjs.utc(get(entity, 'created_at', get(entity, 'createdAt', '1970-01-01'))).toDate(),
        sortType: 'datetime',
        Cell: CellDate,
      },
    ],
    []
  );
  const viewColumns = useMemo(
    () => [
      {
        Header: 'Trait',
        accessor: 'label',
        cellJustify: 'center',
      },
      {
        Header: 'Percentile',
        accessor: (row) => row.score.toFixed(2),
        cellJustify: 'center',
      },
    ],
    []
  );
  const comparisonColumns = useMemo(
    () => [
      {
        Header: 'Trait',
        accessor: 'label',
        cellJustify: 'center',
      },
      {
        Header: 'Variance',
        accessor: (row) => row.variance.toFixed(2),
        cellJustify: 'center',
      },
    ],
    []
  );

  /* eslint-disable react/prop-types */

  const data = useMemo(
    () =>
      segments.map((segment) => ({
        ...segment,
        subRows: segment.clusters.filter(({ filter }) => filter.match(/CLUSTERING_V\d+\/\d+\/SELECTED/)),
      })),
    [segments]
  );

  const getData = useCallback(() => {
    fetchSegments({
      filters: [
        {
          name: 'filter',
          op: 'not_in',
          val: [
            'CLUSTERING_V3/2',
            'CLUSTERING_V3/3',
            'CLUSTERING_V3/4',
            'CLUSTERING_V3/5',
            'CLUSTERING_V3/6',
            'CLUSTERING_V3/2/SELECTED',
            'CLUSTERING_V3/3/SELECTED',
            'CLUSTERING_V3/4/SELECTED',
            'CLUSTERING_V3/5/SELECTED',
            'CLUSTERING_V3/6/SELECTED',
          ],
        },
      ],
      order_by: [
        {
          field: 'created_at',
          direction: 'desc',
        },
      ],
    });
  }, [fetchSegments]);

  const hasData = fetchSegmentsStatus === CONSTANTS.API.SUCCEEDED && data && data.length > 0;

  useEffect(() => {
    if (selectedSegmentId) {
      fetchHighValueWords(selectedSegmentId.segmentId);
    }
    if (selectedClusterId) {
      fetchHighValueWords(selectedClusterId.segmentId, selectedClusterId.clusterId);
    }
  }, [selectedSegmentId, selectedClusterId, fetchHighValueWords]);

  useEffect(() => {
    setShowProgressBar(isAnySelected && copyPxScores.length !== 0);
  }, [isAnySelected, copyPxScores]);

  /*
   * Fetch data whenever the tenant or params change
   */
  useEffect(() => {
    if (tenant) {
      deselectAllGridRows();
      getData();
    }
  }, [tenant, deselectAllGridRows, getData]);

  const chartsData = useMemo(() => {
    if (segments.length === 0 && copyPxScores.length === 0) {
      return {
        objects: [],
        top7Labels: [],
        top7Series: [],
        top7Strength: [],
        top7Table: [],
        uniqueDatasetIds: [],
      };
    }
    const objects = [];
    segments
      .filter(({ id }) => selectedSegmentIds.find(({ segmentId }) => segmentId === id))
      .filter(
        ({ pxScores }) =>
          pxScores && pxScores.length >= CONSTANTS.FINGERPRINT.PX_FACTORS + CONSTANTS.FINGERPRINT.PX_FACETS
      )
      .forEach(({ pxScores, ...rest }) => {
        const pxScoresCopy = pxScores.slice();
        pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
        objects.push({
          ...rest,
          pxScores: pxScoresCopy.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted'),
        });
      });
    segments
      .filter(({ id }) => selectedClusterIds.find(({ segmentId }) => segmentId === id))
      .map(({ clusters }) => clusters)
      .reduce((accum, clusters) => [...accum, ...clusters], [])
      .filter(({ id }) => selectedClusterIds.find(({ clusterId }) => clusterId === id))
      .filter(
        ({ pxScores }) =>
          pxScores && pxScores.length >= CONSTANTS.FINGERPRINT.PX_FACTORS + CONSTANTS.FINGERPRINT.PX_FACETS
      )
      .forEach(({ pxScores, ...rest }) => {
        const pxScoresCopy = pxScores.slice();
        pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
        objects.push({
          ...rest,
          pxScores: pxScoresCopy.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted'),
        });
      });
    if (copyPxScores.length >= CONSTANTS.FINGERPRINT.PX_FACTORS + CONSTANTS.FINGERPRINT.PX_FACETS) {
      const pxScoresCopy = copyPxScores.slice();
      pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
      objects.push({
        name: 'Text',
        pxScores: pxScoresCopy.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted'),
        theme: 'copy',
      });
    }
    if (objects.length === 0) {
      return {
        objects,
        top7Labels: [],
        top7Series: [],
        top7Strength: [],
        top7Table: [],
        uniqueDatasetIds: [],
      };
    }
    const traitsScores = [];
    const uniqueDatasetIds = [];
    const traits = objects[0].pxScores.map(({ trait }) => trait);
    for (let i = 0; i < traits.length; i += 1) {
      const scores = objects.map(({ pxScores }) => pxScores[i].score);
      traitsScores.push({
        trait: traits[i],
        scores,
        order:
          scores.length > 1
            ? mode === 'DIVERGENT_TRAITS'
              ? variance(...scores) || 0.01
              : Math.abs(50 - scores[0])
            : Math.abs(50 - scores[0]),
        varianceScore: scores.length > 1 ? variance(...scores) || 0.01 : Math.abs(50 - scores[0]),
        value: Math.abs(50 - scores[0]),
      });
    }
    traitsScores.sort(({ order: orderA }, { order: orderB }) => orderB - orderA);
    // Isolate the labels
    const labels = traitsScores.map(({ trait }) => trait);
    const strength = traitsScores.map(({ value, varianceScore }) => (objects.length === 1 ? value : varianceScore));
    const series = [];
    for (let i = 0; i < objects.length; i += 1) {
      if (objects[i].dataset && !uniqueDatasetIds.includes(objects[i].dataset.id)) {
        uniqueDatasetIds.push(objects[i].dataset.id);
      }
      series.push({
        name: objects[i].filter === 'POPULATION_SNAPSHOT' ? objects[i].dataset.name : objects[i].name,
        values: traitsScores.map(({ scores }) => scores[i]),
        theme: objects[i].theme,
      });
    }
    const top7Labels = labels.slice(0, 7);
    const top7Series = series.map(({ name, theme, values }) => ({
      name,
      theme,
      values: values.slice(0, 7),
    }));
    const top7Strength = strength.slice(0, 7);
    const top7Table = top7Series[0].values.map((value, index) => ({
      label: labels[index].trait,
      score: value,
      variance: top7Strength[index],
    }));
    return {
      objects,
      top7Labels,
      top7Series,
      top7Strength,
      top7Table,
      uniqueDatasetIds,
    };
  }, [segments, selectedSegmentIds, copyPxScores, mode, selectedClusterIds]);

  const DEFAULT_SEGMENT_SCORES = [];

  const progressBarStops = DEFAULT_THEME.heatmap;
  const progressBarEnd = (scor) => {
    const s = scor >= '100' ? progressBarStops.length - 1 : Math.floor((scor * progressBarStops.length) / 100);
    const m = Math.floor((scor * progressBarStops.length) / 200);
    return `linear-gradient(to right, ${progressBarStops[0]}, ${progressBarStops[m]}  50%,${progressBarStops[s]})`;
  };

  return (
    <Container h="100%" w="100%" minW="100%">
      <TimedPopup
        isBlocked={() => localStorage.getItem('copy_px_timed_popup')}
        delay={1000}
        onClick={() => null}
        block={() => localStorage.setItem('copy_px_timed_popup', true)}
        content={
          <Div d="flex" m={{ b: '1rem' }} flexDir="column">
            <Text p={{ t: '0.25rem', b: '0.35rem' }} textSize="title">
              IMPORTANT
            </Text>
            <Text p={{ t: '0.25rem' }} textSize="subheader">
              In addition to using the percentage match bar, please ensure the output also feels more specific and
              interesting. For best results, generate and A/B test several personalized variants.
            </Text>
          </Div>
        }
      />
      {showProgressBar ? (
        <Row h="3.5625rem" m={{ t: '1em' }} p="0">
          <Col h="2.5rem" w="100%" m={{ b: '1em' }} gutterWidth="0">
            <Div w="100%" h="100%" bg="white" rounded="md" pos="relative" border="0 solid">
              <ProgressBar bg={progressBarEnd(score)} width={score} h="2.5rem" top="0" left="0" transition="width 1.5s">
                <Text
                  pos="absolute"
                  top="0.75rem"
                  right={score < 50 ? (score < 10 ? '-2.5rem' : '-3.5rem') : '1rem'}
                  textColor={score < 50 ? 'black' : 'white'}
                >
                  {score < 0 ? '0.0' : score.toFixed(1)}%
                </Text>
              </ProgressBar>
            </Div>
          </Col>
        </Row>
      ) : (
        <Row h="3.5625rem" m={{ t: '1rem' }}>
          <Col h="2.5rem" w="100%" m={{ b: '1rem' }} />
        </Row>
      )}
      <Row h="calc(100% - 3.5625rem)">
        <Col h="100%" size="6" gutterWidth="1.25rem">
          <Div h="100%">
            <Div h="40%" bg="white" rounded="md" p={{ x: '1.5rem', y: '1.25rem' }}>
              <Text textSize="title" textWeight="normal" textColor="tabSelected" p={{ b: '0.5rem' }}>
                Enter Text
              </Text>
              <PxTextEditor
                comparisonPxScores={
                  selectedSegmentId
                    ? segments.find(({ id }) => selectedSegmentId.segmentId === id).pxScores
                    : selectedClusterId
                    ? segments
                        .find(({ id }) => selectedClusterId.segmentId === id)
                        .clusters.find(({ id }) => selectedClusterId.clusterId === id).pxScores
                    : DEFAULT_SEGMENT_SCORES
                }
                onPxScoresChange={(pxScores) => {
                  setCopyPx(pxScores);
                }}
                onSimilarityChange={(l, s, h) => {
                  setScore(((s - l) * 100) / (h - l));
                }}
                onModelChange={(m) => {
                  setModel(m);
                }}
                h="calc(100% - 2.75em)"
                enableDiagnostics={role === CONSTANTS.ROLE.SUPER_ADMIN}
                ref={pxTextEditorRef}
              />
            </Div>
            <Div pos="relative" h="calc(60% - 1.25rem)" m={{ t: '1.25rem' }}>
              <ApiWrapper hasData={hasData} getData={getData} apiStatus={fetchSegmentsStatus}>
                <Div m={{ y: '1rem' }} p={{ x: '1.5rem' }} bg="white" rounded="md" h="100%">
                  <Text textSize="title" textWeight="normal" textColor="tabSelected" p={{ t: '1.25rem', b: '0.5rem' }}>
                    Choose Dataset
                  </Text>
                  <Div overflow="auto" p="0" m="0" h="calc(100% - 4rem)">
                    <Table
                      columns={columns}
                      data={data}
                      gridState={gridState}
                      controlledGridState={controlledGridState}
                      rawData={segments}
                      enableSelection={({ original }) =>
                        original.pxScores &&
                        original.pxScores.length > 0 &&
                        original.matched >= CONSTANTS.MIN_SEGMENT_MATCHES
                      }
                      selectionTooltip={({ original }) =>
                        original.pxScores &&
                        original.pxScores.length > 0 &&
                        original.matched >= CONSTANTS.MIN_SEGMENT_MATCHES
                          ? 'Select to see traits and candidate words.'
                          : `Under ${CONSTANTS.MIN_SEGMENT_MATCHES} matches; analytics unavailable.`
                      }
                      onRowSelect={(row) => {
                        selectGridRow(row);
                      }}
                      onRowDeselect={(row) => {
                        deselectGridRow(row);
                      }}
                      hasSelection
                      hasExpansion
                      maxSelection={maxGridRowSelection}
                      setGridState={(state) => {
                        setGridState(state);
                      }}
                      subRowsColsIndentIndexes={[1, 2, 3]}
                      widths="2.75rem 2rem 5fr 2fr 3fr"
                      p={{ y: '0' }}
                    />
                  </Div>
                </Div>
              </ApiWrapper>
            </Div>
          </Div>
        </Col>
        <Col h="100%" size="6" gutterWidth="1.25rem">
          {isAnySelected || copyPxScores.length > 0 ? (
            <Div d="flex" flexDir="column" w="100%" h="100%" justify="space-between">
              <Div bg="white" p={{ x: '1.5rem', t: '1.25rem', b: '0.75rem' }} rounded="md" h="60%">
                <Div w="100%" m={{ x: 'auto' }} h="100%" flexGrow="1">
                  <Radar
                    title={
                      chartsData.objects.length === 1
                        ? 'Top 7 Divergent Traits With US Population'
                        : 'Top 7 Divergent Traits Between Text and Dataset'
                    }
                    labels={chartsData.top7Labels}
                    series={chartsData.top7Series}
                    options={{
                      legend: {
                        show: true,
                        top: 'bottom',
                        padding: [0, 0, 30, 0],
                        textStyle: { fontSize: 14 },
                        itemGap: 24,
                      },
                      radar: {
                        radius: '72%',
                        center: ['50%', '44.5%'],
                      },
                      nameGap: 50,
                      label: {
                        textStyle: {
                          fontSize: 14,
                        },
                      },
                    }}
                    relative
                    showRangeControl
                    controls={
                      chartsData.objects.length > 1
                        ? [
                            <Button
                              buttonStyle="primary"
                              h="2.5rem"
                              m={{ x: '0.5rem' }}
                              onClick={() => {
                                mode === 'DIVERGENT_TRAITS' ? setMode('DATASET_TRAITS') : setMode('DIVERGENT_TRAITS');
                              }}
                            >
                              {mode === 'DIVERGENT_TRAITS' ? 'ANCHOR TO DATASET' : 'MOST DIVERGENT TRAITS'}
                            </Button>,
                          ]
                        : []
                    }
                    onThemeChange={() => null}
                    showPalette={false}
                  />
                </Div>
              </Div>
              {isAnySelected ? (
                <Div
                  h="calc(40% - 1.25rem)"
                  bg="white"
                  p={{ x: '1.5rem', y: '1.25rem' }}
                  m={{ t: '1.25rem' }}
                  rounded="md"
                >
                  <Text textSize="title" textWeight="400" textColor="tabSelected" p={{ b: '.5rem' }}>
                    High Value Words
                  </Text>
                  <Div pos="relative" w="100%" h="calc(100% - 2rem)" m={{ r: '-10px' }} overflow="overlay">
                    {fetchHighValueWordsStatus === CONSTANTS.API.STARTED ? (
                      <Message />
                    ) : fetchHighValueWordsStatus === CONSTANTS.API.SUCCEEDED ? (
                      highValueWords && highValueWords.words && highValueWords.words.length > 0 ? (
                        <Div d="flex" flexWrap="wrap" justify="flex-start" m={{ x: '-.35rem' }} w="calc(100% - .7rem)">
                          {highValueWords.words.map(({ word }) => (
                            <Tag
                              m={{ x: '0.35rem', y: '0.25rem' }}
                              p={{ x: '.75rem', y: '0' }}
                              rounded="sm"
                              border="2px solid"
                              borderColor={
                                selectedSegmentId
                                  ? getSegmentTheme(segments, selectedSegmentId.segmentId)
                                  : selectedClusterId
                                  ? getClusterTheme(segments, selectedClusterId.segmentId, selectedClusterId.clusterId)
                                  : 'theme_pinpoint'
                              }
                              cursor="pointer"
                              onClick={() => {
                                pxTextEditorRef.current.addWord(word);
                              }}
                              data-tip="Add word to text area"
                            >
                              <Text textSize="subheader" textColor="tabSelected" textWeight="400">
                                {word}
                              </Text>
                            </Tag>
                          ))}
                        </Div>
                      ) : (
                        <Div
                          m={{ x: '0.5rem', y: '0.25rem' }}
                          textStyle="body"
                          textColor="tabSelected"
                          style={{
                            fontStyle: 'italic',
                          }}
                        >
                          No high value words to show now. Please try again later
                        </Div>
                      )
                    ) : null}
                  </Div>
                </Div>
              ) : null}
            </Div>
          ) : (
            <Div
              d="flex"
              w="100%"
              h="25%"
              justify="center"
              align="center"
              bg="white"
              p={{ x: '1.5rem', y: '1.25rem' }}
              rounded="md"
            >
              <Text textSize="subhead">
                <Switch status={model}>
                  <Div value="BERTTWEET_MODEL">
                    Please enter 6 or more words and select a dataset to see psychological fit.
                  </Div>
                  <Div value="WORD_2_VEC">Please enter text and select a dataset to see psychological fit.</Div>
                  <Div default>Please wait ...</Div>
                </Switch>
              </Text>
            </Div>
          )}
        </Col>
      </Row>
    </Container>
  );
}

const mapStateToProps = ({ app, bc, copyPx }) => ({
  app,
  bc,
  copyPx,
});

const mapDispatchToProps = (dispatch) => ({
  setDatasetId: (datasetId) => dispatch(bcActions.setDatasetId(datasetId)),
  setDatasetName: (name) => dispatch(bcActions.setDatasetName(name)),
  reset: () => dispatch(bcActions.reset()),
  fetchDatasets: (query) => dispatch(appActions.fetchDatasets(query)),
  fetchSegments: (query) => dispatch(copyPxActions.fetchSegments(query)),
  fetchHighValueWords: (...params) => dispatch(copyPxActions.fetchHighValueWords(...params)),
  setSegmentId: (segmentId) => dispatch(appActions.setSegmentId(segmentId)),
  selectGridRow: (segmentId) => dispatch(copyPxActions.selectGridRow(segmentId)),
  deselectGridRow: (segmentId) => dispatch(copyPxActions.deselectGridRow(segmentId)),
  setCopyPx: (...params) => dispatch(copyPxActions.setCopyPx(...params)),
  setGridState: (state) => dispatch(copyPxActions.setGridState(state)),
  deselectAllGridRows: (state) => dispatch(copyPxActions.deselectAllGridRows(state)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  editSegment: (...params) => dispatch(copyPxActions.editSegment(...params)),
});

CopyPx.propTypes = {
  app: PropTypes.shape({
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ).isRequired,
    fetchDatasetsStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      tenant: PropTypes.string.isRequired,
    }),
    flyoverPinStatus: PropTypes.string.isRequired,
  }).isRequired,
  copyPx: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        pxScores: PropTypes.arrayOf(PropTypes.shape({})),
        dataset: PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      })
    ).isRequired,
    selectedSegmentIds: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    selectedClusterIds: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fetchSegmentsStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    gridState: PropTypes.shape({}).isRequired,
    controlledGridState: PropTypes.shape({}).isRequired,
    maxGridRowSelection: PropTypes.number.isRequired,
  }).isRequired,
  fetchHighValueWords: PropTypes.func.isRequired,
  fetchSegments: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  setSegmentId: PropTypes.func.isRequired,
  selectGridRow: PropTypes.func.isRequired,
  deselectGridRow: PropTypes.func.isRequired,
  setCopyPx: PropTypes.func.isRequired,
  setGridState: PropTypes.func.isRequired,
};

function CellText({ value }) {
  return (
    <Text
      m={{ r: '0.5rem' }}
      data-tip={value}
      textSize="small"
      style={{
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Text>
  );
}

function CellDate({ value }) {
  return (
    <Text
      data-tip={dayjs(value).format('HH:mm [GMT]Z (zzz) — ddd, MMM D, YYYY')}
      m={{ r: '0.5rem' }}
      textSize="small"
      style={{
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {dayjs(value).format('MMM DD, YYYY')}
    </Text>
  );
}

function CellTag({ value }) {
  if (!CONSTANTS.SEGMENT.TAGS.includes(value)) return <Tag bg="transparent">&ndash; &ndash; &ndash;</Tag>;
  const { color, name, viewBox } = getSegmentTagStyles(value);
  return (
    <Tag textColor={color} borderColor={color} border="1px solid" h="1.125rem">
      <Icon name={name} size=".875rem" viewBox={viewBox} m={{ r: '.5em' }} color={color} />
      <Text textSize="small">{value}</Text>
    </Tag>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(GraphicPage(CopyPx));
